import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const BLOCK_BEGIN_RE = /\\begin\{[a-zA-Z*]+\}[\s\S]*?\\end\{[a-zA-Z*]+\}/g;
const BARE_MATH_RE = /\\(?:int|iint|iiint|oint|sum|prod|lim|frac|dfrac|sqrt|left|right|cdot|partial|infty|alpha|beta|gamma|delta|theta|pi|sigma|omega|leq|geq|neq|to|begin|end|mathbb|mathbf|mathrm|mathcal|vec|hat|bar|overline)\b(?:[^\s,.;:!?)]|\s(?![A-Za-zÇĞİÖŞÜçğıöşü]{2,}))*|[∫∑√∞≤≥≠→][^\s,.;:!?)]*/g;
const PLAIN_FORMULA_RE = /\bf[xyz]\s*=\s*[^,.;!?]+(?:,\s*f[xyz]\s*=\s*[^,.;!?]+)*/g;
const PLAIN_POWER_RE = /[A-Za-z0-9)\]}]+(?:\s*)\^\([^)]+\)|[A-Za-z0-9)\]}]+(?:\s*)\^[A-Za-z0-9]+/g;
const PLAIN_SYMBOLIC_RE = /(?:\\partial\s*[A-Za-z](?:\s*\/\s*\\partial\s*[A-Za-z])?|[A-Za-z]+_[A-Za-z0-9]+(?:\([^)]*\))?(?:(?:sin|cos|tan|cot|sec|csc|ln|log)?\\[A-Za-z]+)?|(?:sin|cos|tan|cot|sec|csc|ln|log)\\[A-Za-z]+)(?:\s*[+\-=]\s*(?:\\partial\s*[A-Za-z](?:\s*\/\s*\\partial\s*[A-Za-z])?|[A-Za-z]+_[A-Za-z0-9]+(?:\([^)]*\))?(?:(?:sin|cos|tan|cot|sec|csc|ln|log)?\\[A-Za-z]+)?|(?:sin|cos|tan|cot|sec|csc|ln|log)\\[A-Za-z]+|[A-Za-z0-9)\]}]+\s*\^(?:\([^)]+\)|[A-Za-z0-9]+)))*/g;
const LATEX_COMMAND_RE = /\\{2,4}(iiint|iint|oint|int|sum|prod|lim|frac|dfrac|sqrt|left|right|cdot|partial|nabla|infty|alpha|beta|gamma|delta|theta|pi|sigma|omega|leq|geq|neq|to|begin|end|mathbb|mathbf|mathrm|mathcal|vec|hat|bar|overline)\b/g;

const normalizeMathText = (value) => {
  let normalized = String(value);
  const unicodeMap = {
    '∫∫∫': '\\iiint ',
    '∫∫': '\\iint ',
    '∫': '\\int ',
    '∑': '\\sum ',
    '∏': '\\prod ',
    '√': '\\sqrt ',
    '∞': '\\infty ',
    '≤': '\\leq ',
    '≥': '\\geq ',
    '≠': '\\neq ',
    '→': '\\to ',
    '∂': '\\partial ',
    '∇': '\\nabla ',
    'π': '\\pi ',
    'θ': '\\theta ',
    'α': '\\alpha ',
    'β': '\\beta ',
  };

  Object.entries(unicodeMap).forEach(([symbol, latex]) => {
    normalized = normalized.split(symbol).join(latex);
  });

  return normalized
    .replace(LATEX_COMMAND_RE, '\\$1')
    .replace(/`([^`]*?(?:\\|[=^_{}()]|[∫∑√∞≤≥≠→∂πθαβ]|[A-Za-z]\s*\^)[^`]*?)`/g, '$1');
};

const normalizePlainPower = (value) => (
  value
    .replace(/([A-Za-z0-9)\]}]+)\s*\^\(([^)]+)\)/g, '$1^{$2}')
    .replace(/([A-Za-z0-9)\]}]+)\s*\^([A-Za-z0-9]+)/g, '$1^{$2}')
);

const normalizePlainSymbolic = (value) => (
  normalizePlainPower(value)
    .replace(/([A-Za-z])\u20d7/g, '\\vec{$1}')
    .replace(/\\partial\s*([A-Za-z])\s*\/\s*\\partial\s*([A-Za-z])/g, '\\frac{\\partial $1}{\\partial $2}')
    .replace(/\b(f)([xyz])\b/g, '$1_$2')
    .replace(/\b(ln|log|sin|cos|tan|cot|sec|csc)([A-Za-z])\b/g, '\\$1 $2')
    .replace(/\b(sin|cos|tan|cot|sec|csc|ln|log)(?=\\|[A-Za-z({])/g, '\\$1')
    .replace(/([A-Za-z]+)_([A-Za-z0-9]+)/g, '$1_{$2}')
    .replace(/\\partial\s*([A-Za-z])/g, '\\partial $1')
);

const normalizeLatexExpression = (value) => normalizePlainSymbolic(value);

const renderLatex = (math, displayMode, key) => {
  try {
    const html = katex.renderToString(normalizeLatexExpression(math).trim(), {
      displayMode,
      throwOnError: false,
      output: 'html',
      strict: false,
      trust: false,
    });
    return (
      <span
        key={key}
        className={displayMode ? 'math-block' : 'math-inline'}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  } catch {
    return <span key={key}>{math}</span>;
  }
};

const renderPlainTextWithBareMath = (text, keyPrefix) => {
  const nodes = [];
  let cursor = 0;
  let match;

  const pushText = (value, key) => {
    if (value) nodes.push(<span key={key}>{value}</span>);
  };

  BLOCK_BEGIN_RE.lastIndex = 0;
  const blockRanges = [];
  while ((match = BLOCK_BEGIN_RE.exec(text)) !== null) {
    blockRanges.push({ start: match.index, end: match.index + match[0].length, math: match[0], display: true });
  }

  if (blockRanges.length > 0) {
    blockRanges.forEach((range, index) => {
      const before = text.slice(cursor, range.start);
      pushText(before, `${keyPrefix}-before-${index}`);
      nodes.push(renderLatex(range.math, range.display, `${keyPrefix}-block-${index}`));
      cursor = range.end;
    });
    const rest = text.slice(cursor);
    if (rest) nodes.push(...renderPlainTextWithBareMath(rest, `${keyPrefix}-rest`));
    return nodes;
  }

  cursor = 0;
  BARE_MATH_RE.lastIndex = 0;
  let foundBareMath = false;
  while ((match = BARE_MATH_RE.exec(text)) !== null) {
    const raw = match[0].trim();
    if (!raw || raw.length < 2) continue;
    foundBareMath = true;
    pushText(text.slice(cursor, match.index), `${keyPrefix}-text-${match.index}`);
    nodes.push(renderLatex(raw, false, `${keyPrefix}-math-${match.index}`));
    cursor = match.index + match[0].length;
  }

  if (foundBareMath) {
    pushText(text.slice(cursor), `${keyPrefix}-tail`);
    return nodes;
  }

  nodes.length = 0;
  cursor = 0;
  PLAIN_FORMULA_RE.lastIndex = 0;
  while ((match = PLAIN_FORMULA_RE.exec(text)) !== null) {
    const raw = match[0].trim();
    if (!raw || raw.length < 3) continue;
    pushText(text.slice(cursor, match.index), `${keyPrefix}-formula-text-${match.index}`);
    nodes.push(renderLatex(raw, false, `${keyPrefix}-formula-${match.index}`));
    cursor = match.index + match[0].length;
  }
  if (nodes.length > 0) {
    pushText(text.slice(cursor), `${keyPrefix}-formula-tail`);
    return nodes;
  }

  cursor = 0;
  PLAIN_SYMBOLIC_RE.lastIndex = 0;
  while ((match = PLAIN_SYMBOLIC_RE.exec(text)) !== null) {
    const raw = match[0].trim();
    if (!raw || raw.length < 3) continue;
    pushText(text.slice(cursor, match.index), `${keyPrefix}-symbol-text-${match.index}`);
    nodes.push(renderLatex(normalizePlainSymbolic(raw), false, `${keyPrefix}-symbol-${match.index}`));
    cursor = match.index + match[0].length;
  }
  if (nodes.length > 0) {
    pushText(text.slice(cursor), `${keyPrefix}-symbol-tail`);
    return nodes;
  }

  cursor = 0;
  PLAIN_POWER_RE.lastIndex = 0;
  while ((match = PLAIN_POWER_RE.exec(text)) !== null) {
    const raw = match[0].trim();
    if (!raw || raw.length < 3) continue;
    pushText(text.slice(cursor, match.index), `${keyPrefix}-power-text-${match.index}`);
    nodes.push(renderLatex(normalizePlainPower(raw), false, `${keyPrefix}-power-${match.index}`));
    cursor = match.index + match[0].length;
  }
  pushText(text.slice(cursor), `${keyPrefix}-power-tail`);

  return nodes.length > 0 ? nodes : [<span key={`${keyPrefix}-plain`}>{text}</span>];
};

/**
 * Matematiksel ifadeler içeren metni render eder.
 * $$...$$ → blok (display) mod
 * $...$ → satır içi (inline) mod
 * \[...\] → blok (display) mod
 * \(...\) → satır içi (inline) mod
 * Diğer metin → düz metin
 */
function MathText({ text, children, block = false }) {
  const value = text !== undefined ? text : children;
  if (!value) return null;
  const content = normalizeMathText(value);

  // $$...$$, $...$, \[...\] ve \(...\) ifadelerini ayır
  const parts = content.split(/(\$\$[^$]*?\$\$|\\\[[^\]]*?\\\]|\\\([^)]*?\\\)|\$[^$\n]*?\$)/g);

  if (parts.length === 1) {
    return <span>{renderPlainTextWithBareMath(content, 'plain')}</span>;
  }

  return (
    <span>
      {parts.map((part, i) => {
        if (part.startsWith('$$') && part.endsWith('$$') && part.length > 4) {
          const math = part.slice(2, -2).trim();
          return renderLatex(math, true, i);
        }

        if (part.startsWith('\\[') && part.endsWith('\\]') && part.length > 4) {
          const math = part.slice(2, -2).trim();
          return renderLatex(math, true, i);
        }

        if (part.startsWith('\\(') && part.endsWith('\\)') && part.length > 4) {
          const math = part.slice(2, -2).trim();
          return renderLatex(math, false, i);
        }

        if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
          const math = part.slice(1, -1).trim();
          return renderLatex(math, block, i);
        }

        return <span key={i}>{renderPlainTextWithBareMath(part, `part-${i}`)}</span>;
      })}
    </span>
  );
}

export default MathText;
